import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { FaArrowLeft } from "react-icons/fa";
import "../css/BusinessTrends.css";

const BusinessTrends = () => {
  const [monthlyData, setMonthlyData] = useState([]); // [{ date:"YYYY-MM", kidCount }]
  const [dailyData, setDailyData] = useState([]); // [{ date:"YYYY-MM-DD", kidCount }]
  const [privateEventsData, setPrivateEventsData] = useState([]); // [{ date:"YYYY-MM", privateEvents }]
  const [privateEventsDaily, setPrivateEventsDaily] = useState([]); // [{ date:"YYYY-MM-DD", privateEvents }]
  const [selectedMonth, setSelectedMonth] = useState(null); // "YYYY-MM"
  const [viewingPrivateEvents, setViewingPrivateEvents] = useState(false);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("thisYear");
  const currentYear = new Date().getFullYear();

  // -------------------------------
  // Fetch & aggregate (unchanged logic; cleaned a bit)
  // -------------------------------
  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const txRef =
          range === "allTime"
            ? collection(db, "transactions")
            : query(collection(db, "transactions"), where("Year", "==", currentYear));
        const evRef =
          range === "allTime"
            ? collection(db, "privateEvents")
            : query(collection(db, "privateEvents"), where("year", "==", currentYear));
        const [transactionsSnapshot, eventsSnapshot] = await Promise.all([
          getDocs(txRef),
          getDocs(evRef),
        ]);

        const tx = transactionsSnapshot.docs.map((d) => d.data());
        const ev = eventsSnapshot.docs.map((d) => d.data());

        const trends = {}; // month -> kids
        const dailyTrends = {}; // day -> kids
        const privateTrends = {}; // month -> count
        const privateDailyTrends = {}; // day -> count

        const estimateKidCount = (playCost) => {
          if (!playCost || playCost <= 0) return 0;
          const prices = [30, 25, 20];
          let count = 0;
          let remaining = playCost;
          for (const p of prices) {
            if (remaining >= p) {
              remaining -= p;
              count++;
            } else break;
          }
          count += Math.floor(remaining / 20);
          return count;
        };

        tx.forEach((t) => {
          const year = t.Year;
          const month = t.Month;
          if (!year || !month) return;
          // Guard against locale dates; use stored Month/Year
          const monthKey = `${year}-${String(month).padStart(2, "0")}`;

          // For daily key, try to parse MM/DD/YYYY
          let dayKey = null;
          if (t.Date) {
            const parts = String(t.Date).split("/");
            if (parts.length === 3) {
              const m = Number(parts[0]);
              const d = Number(parts[1]);
              const y = Number(parts[2]);
              if (m && d && y) {
                dayKey = `${y}-${String(m).padStart(2, "0")}-${String(
                  d
                ).padStart(2, "0")}`;
              }
            }
          }

          const kids = t.KidCount ?? estimateKidCount(t.PlayCost || 0);
          trends[monthKey] = (trends[monthKey] || 0) + kids;
          if (dayKey) dailyTrends[dayKey] = (dailyTrends[dayKey] || 0) + kids;
        });

        ev.forEach((e) => {
          const year = e.year;
          const month = e.month;
          if (!year || !month) return;
          const monthKey = `${year}-${String(month).padStart(2, "0")}`;

          let dayKey = null;
          if (e.startTime) {
            const d = new Date(e.startTime);
            if (!isNaN(d)) {
              const y = d.getFullYear();
              const m = d.getMonth() + 1;
              const day = d.getDate();
              dayKey = `${y}-${String(m).padStart(2, "0")}-${String(
                day
              ).padStart(2, "0")}`;
            }
          }

          // Count any item with a packageType as a private event
          privateTrends[monthKey] = (privateTrends[monthKey] || 0) + 1;
          if (dayKey)
            privateDailyTrends[dayKey] = (privateDailyTrends[dayKey] || 0) + 1;
        });

        const toSortedMonthArray = (obj, valueKey) =>
          Object.entries(obj)
            .map(([date, val]) => ({ date, [valueKey]: val }))
            .sort((a, b) => {
              const [ya, ma] = a.date.split("-").map(Number);
              const [yb, mb] = b.date.split("-").map(Number);
              return new Date(ya, ma - 1) - new Date(yb, mb - 1);
            });

        setMonthlyData(toSortedMonthArray(trends, "kidCount"));
        setPrivateEventsData(
          toSortedMonthArray(privateTrends, "privateEvents")
        );
        setDailyData(
          Object.entries(dailyTrends).map(([date, kidCount]) => ({
            date,
            kidCount,
          }))
        );
        setPrivateEventsDaily(
          Object.entries(privateDailyTrends).map(([date, privateEvents]) => ({
            date,
            privateEvents,
          }))
        );
      } catch (err) {
        console.error("Error fetching transaction data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [currentYear, range]);

  // -------------------------------
  // Helpers
  // -------------------------------
  const fmtMonth = (ym) => {
    // "YYYY-MM" -> "MMM yyyy"
    const [y, m] = ym.split("-").map(Number);
    const d = new Date(y, m - 1, 1);
    return d.toLocaleString(undefined, { month: "short", year: "numeric" });
  };

  const getBarClass = (count, maxCount, palette) => {
    // palette: "month" | "day" | "privateMonth" | "privateDay"
    if (!maxCount)
      return palette === "privateMonth" || palette === "privateDay"
        ? "bar-private-low"
        : palette === "day"
        ? "bar-day-low"
        : "bar-month-low";

    const pct = count / maxCount;
    const tier = pct > 0.66 ? "high" : pct > 0.33 ? "medium" : "low";

    if (palette === "privateMonth" || palette === "privateDay") {
      return `bar-private-${tier}`;
    }
    if (palette === "day") return `bar-day-${tier}`;
    return `bar-month-${tier}`;
  };

  const handleBarClick = (data, isPrivateEvents) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const clickedMonth = data.activePayload[0].payload.date;
      setSelectedMonth(clickedMonth);
      setViewingPrivateEvents(isPrivateEvents);
    }
  };

  const monthDaysData = useMemo(() => {
    if (!selectedMonth) return [];
    const [y, m] = selectedMonth.split("-").map(Number);
    const daysInMonth = new Date(y, m, 0).getDate();
    const arr = Array.from({ length: daysInMonth }, (_, i) => {
      const day = String(i + 1).padStart(2, "0");
      const key = `${selectedMonth}-${day}`;

      return viewingPrivateEvents
        ? {
            date: key,
            count:
              privateEventsDaily.find((d) => d.date === key)?.privateEvents ||
              0,
          }
        : {
            date: key,
            count: dailyData.find((d) => d.date === key)?.kidCount || 0,
          };
    });
    return arr;
  }, [selectedMonth, viewingPrivateEvents, privateEventsDaily, dailyData]);

  const maxForSelectedMonth = useMemo(() => {
    if (!monthDaysData.length) return 1;
    return Math.max(...monthDaysData.map((d) => d.count), 1);
  }, [monthDaysData]);

  // For monthly overviews
  const monthlyKidsMax = Math.max(
    ...monthlyData.map((d) => d.kidCount || 0),
    1
  );
  const monthlyPrivMax = Math.max(
    ...privateEventsData.map((d) => d.privateEvents || 0),
    1
  );

  // -------------------------------
  // Render
  // -------------------------------
  if (loading) {
    return (
      <div className="business-trends">
        <h2>Business Trends</h2>
        <div className="loading">Loading…</div>
      </div>
    );
  }

  return (
    <div className="business-trends">
      <div className="bt-toolbar">
        <label>
          Range
          <select
            value={range}
            onChange={(event) => {
              setSelectedMonth(null);
              setRange(event.target.value);
            }}
          >
            <option value="thisYear">This Year</option>
            <option value="allTime">All Time</option>
          </select>
        </label>
        {range === "allTime" && (
          <span className="bt-warning">
            All Time reads all historical transactions/events and may use more
            Firestore reads.
          </span>
        )}
      </div>
      {selectedMonth ? (
        <>
          <div className="bt-header">
            <button
              className="bt-back"
              onClick={() => setSelectedMonth(null)}
              aria-label="Back"
            >
              <FaArrowLeft />
            </button>
            <h2>
              {viewingPrivateEvents
                ? "Daily Private Events"
                : "Daily Transactions"}{" "}
              — {fmtMonth(selectedMonth)}
            </h2>
          </div>

          <div className="business-trends-wrapper">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={monthDaysData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => d.split("-")[2]}
                  interval="preserveStartEnd"
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  labelFormatter={(d) => d}
                  formatter={(v) => [
                    v,
                    viewingPrivateEvents ? "Events" : "Kids",
                  ]}
                />
                <Bar dataKey="count" className="bar-clickable">
                  {monthDaysData.map((entry, i) => (
                    <Cell
                      key={i}
                      className={getBarClass(
                        entry.count,
                        maxForSelectedMonth,
                        viewingPrivateEvents ? "privateDay" : "day"
                      )}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : (
        <>
          <h2>Monthly Transactions (Kids)</h2>
          <div className="business-trends-wrapper">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={monthlyData}
                onClick={(d) => handleBarClick(d, false)}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={fmtMonth}
                  interval="preserveStartEnd"
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  labelFormatter={fmtMonth}
                  formatter={(v) => [v, "Kids"]}
                />
                <Bar dataKey="kidCount" className="bar-clickable">
                  {monthlyData.map((entry, i) => (
                    <Cell
                      key={i}
                      className={getBarClass(
                        entry.kidCount || 0,
                        monthlyKidsMax,
                        "month"
                      )}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <h2>Monthly Private Events</h2>
          <div className="business-trends-wrapper">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart
                data={privateEventsData}
                onClick={(d) => handleBarClick(d, true)}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={fmtMonth}
                  interval="preserveStartEnd"
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  labelFormatter={fmtMonth}
                  formatter={(v) => [v, "Events"]}
                />
                <Bar dataKey="privateEvents" className="bar-clickable">
                  {privateEventsData.map((entry, i) => (
                    <Cell
                      key={i}
                      className={getBarClass(
                        entry.privateEvents || 0,
                        monthlyPrivMax,
                        "privateMonth"
                      )}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default BusinessTrends;
